import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { Client, Context as UrqlContext } from 'urql';
import { useGetContractInfoQuery, useSignContractMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { getFiles, startDownload } from 'views/utils/download';
import { ContractStatus } from 'constants/contract';
import { useNavigate } from 'react-router-dom';
import { PagePath } from 'constants/pagePath';

export const enum ContractProcedureStep {
  // DocumentCheck,
  Confirm,
  Complete,
  Canceled,
}

type ContractProcedureProps = {
  id: string;
  defaultContractProcedureStep? : ContractProcedureStep;
};

export const useContractProcedure = ({ id , defaultContractProcedureStep}: ContractProcedureProps) => {
  const client = useContext(UrqlContext) as Client;
  const navigate = useNavigate();
  const { showError } = useGlobalToastHandler();
  const [contractProcedureStep, setContractProcedureStep] = useState<ContractProcedureStep>(
    defaultContractProcedureStep? defaultContractProcedureStep : ContractProcedureStep.Confirm ,    
  );

  // 匿名組合契約地位譲渡契約書
  const [openedAssignmentAgreementDocument, setOpenedAssignmentAgreementDocument] = useState<boolean>(false);
  const [checkedAssignmentAgreementDocument, setCheckedAssignmentAgreementDocument] = useState<boolean>(false);

  // 振込依頼書
  const [openedTransferRequestDocument, setOpenedTransferRequestDocument] = useState<boolean>(false);
  const [checkedTransferRequestDocument, setCheckedTransferRequestDocument] = useState<boolean>(false);

  // 契約締結前交付書面
  const [openedContractAgreementDocument, setOpenedContractAgreementDocument] = useState<boolean>(false);
  const [checkedContractAgreementDocument, setCheckedContractAgreementDocument] = useState<boolean>(false);

  const [contractInfo, getContractInfo] = useGetContractInfoQuery({
    variables: {
      id,
    },
    requestPolicy: 'network-only',
  });

  const [_response, signContract] = useSignContractMutation();

  const download = useOnceSubmit<{ id: string; fileName: string }>(
    {
      onSubmit: async (param: { id: string; fileName: string }) => {
        try {
          const files = await getFiles(client, [
            {
              fileName: param.fileName,
              id: param.id,
            },
          ]);
          await startDownload(files);
        } catch (error) {
          showError('ファイルのダウンロードに失敗しました');
        }
      },
    },
    [client, showError],
  );

  const assignmentAgreementDocumentDownload = async () => {
    if (contractInfo.data?.GetContractInfo?.Details?.AssignmentAgreementDocumentInfo) {
      await download({
        id: contractInfo.data?.GetContractInfo?.Details?.AssignmentAgreementDocumentInfo.ID,
        fileName: contractInfo.data?.GetContractInfo?.Details?.AssignmentAgreementDocumentInfo.DisplayName,
      });
      setOpenedAssignmentAgreementDocument(true);
    }
  };

  const transferRequestDocumentDownload = async () => {
    if (contractInfo.data?.GetContractInfo?.Details?.TransferRequestFormDocumentInfo) {
      await download({
        id: contractInfo.data?.GetContractInfo?.Details?.TransferRequestFormDocumentInfo.ID,
        fileName: contractInfo.data?.GetContractInfo?.Details?.TransferRequestFormDocumentInfo.DisplayName,
      });
      setOpenedTransferRequestDocument(true);
    }
  };

  const contractAgreementDocumentDownload = async () => {
    if (contractInfo.data?.GetContractInfo?.Details?.ContractAgreementDocumentInfo) {
      await download({
        id: contractInfo.data?.GetContractInfo?.Details?.ContractAgreementDocumentInfo.ID,
        fileName: contractInfo.data?.GetContractInfo?.Details?.ContractAgreementDocumentInfo.DisplayName,
      });
      setOpenedContractAgreementDocument(true);
    }
  };

  const canSignContract = useMemo(
    () =>
      // openedAssignmentAgreementDocument &&
      checkedAssignmentAgreementDocument &&
      // openedTransferRequestDocument &&
      // checkedTransferRequestDocument &&
      openedContractAgreementDocument &&
      checkedContractAgreementDocument,
    [
      // openedAssignmentAgreementDocument,
      checkedAssignmentAgreementDocument,
      // openedTransferRequestDocument,
      // checkedTransferRequestDocument,
      openedContractAgreementDocument,
      checkedContractAgreementDocument,
    ],
  );

  const onNext = useOnceSubmit<void>(
    {
      onPreviousSubmit: async () => {
        return canSignContract;
      },
      onSubmit: async () => {
        switch (contractProcedureStep) {
          // case ContractProcedureStep.DocumentCheck:
          //   setContractProcedureStep(ContractProcedureStep.Confirm);
          //   break;
          case ContractProcedureStep.Confirm:
            const res = await signContract({
              input: {
                ID: id,
                CheckedAssignmentAgreementDocument : checkedAssignmentAgreementDocument,
                CheckedContractAgreementDocument : checkedContractAgreementDocument,
              },
            });
            if (!res?.data) return;

            if (res.data.SignContract?.Code === 0) {
              getContractInfo()
              setContractProcedureStep(ContractProcedureStep.Complete);
            } else {
              showError(res.data.SignContract?.Message);
            }
            break;
          case ContractProcedureStep.Complete:
            break;
        }
      },
    },
    [canSignContract, contractProcedureStep, signContract, setContractProcedureStep, showError],
  );

  const onBack = useCallback(() => {
    switch (contractProcedureStep) {
      // case ContractProcedureStep.DocumentCheck:
      //   return;
      case ContractProcedureStep.Confirm:
        navigate(PagePath.Home)
        return;
      case ContractProcedureStep.Complete:
        return;
    }
  }, [contractProcedureStep, setContractProcedureStep]);

  useEffect(() => {
    if (!contractInfo.data?.GetContractInfo) return;

    // 既に完了や契約解除になっている場合はStepを変更する
    if (contractInfo.data?.GetContractInfo?.Details?.Status === ContractStatus.Completed) {
      setContractProcedureStep(ContractProcedureStep.Complete);
    } else if (contractInfo.data?.GetContractInfo?.Details?.Status === ContractStatus.Canceled) {
      setContractProcedureStep(ContractProcedureStep.Canceled);
    }
  }, [contractInfo.data?.GetContractInfo, setContractProcedureStep]);

  return {
    contractProcedureStep,
    openedAssignmentAgreementDocument,
    checkedAssignmentAgreementDocument,
    setCheckedAssignmentAgreementDocument,
    assignmentAgreementDocumentDownload,
    openedTransferRequestDocument,
    checkedTransferRequestDocument,
    setCheckedTransferRequestDocument,
    transferRequestDocumentDownload,
    openedContractAgreementDocument,
    checkedContractAgreementDocument,
    setCheckedContractAgreementDocument,
    setOpenedContractAgreementDocument,
    contractAgreementDocumentDownload,
    contractInfo,
    canSignContract,
    onNext,
    onBack,
  };
};
